import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { ChevronLeft } from "lucide-react";
import { forgotpasswordcrew } from "services/authService";

export default function ForgotPasswordPageCrew() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [counter, setCounter] = useState(54);
  const [error, setError] = useState("");
  const [resetToken, setResetToken] = useState("");
  const navigate = useNavigate();

  // VIDEO SEQUENCE
  const videos = [
    "/images/login1.mp4",
    "/images/login3.mp4",
    "/images/login2.mp4"
  ];

  const [currentVideo, setCurrentVideo] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideo((prev) => (prev + 1) % videos.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleSend = async () => {
    if (!email) {
      setError("Email is required");
      return;
    }

    setError("");

    const payload = {
      email: email
    };

    try {
      const response = await forgotpasswordcrew(payload);

      if (response.resetToken) {
        setSent(true);
        setResetToken(response.resetToken);

        let interval = setInterval(() => {
          setCounter((prev) => {
            if (prev === 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.message || "An error occurred, please try again.");
      }
    } catch (error) {
      setError("Something went wrong, please try again later.");
    }
  };

  const handleOpenEmail = () => {
    navigate(`/reset-password?token=${resetToken}`);
  };

  return (
    <div className="w-full h-screen flex overflow-hidden bg-[#fdf9f2]">
      {/* LEFT FORM SECTION (same layout as login) */}
      <div className="w-full lg:w-1/2 flex flex-col h-full px-4 lg:px-14 py-14 lg:py-10">
        {/* TOP: Back + Logo */}
        <div className="w-full flex justify-center">
          <div className="max-w-md w-full flex items-center justify-between mb-8">
            <Link to="/login" className="flex items-center text-gray-600 text-sm">
              <ChevronLeft size={18} className="mr-1" /> Back
            </Link>
            <img
              src="/images/Container.png"
              alt="Beige Logo"
              className="w-24 object-contain"
            />
          </div>
        </div>

        {/* FORM SECTION */}
        <div className="flex flex-col flex-grow justify-center max-w-md w-full mx-auto">
          {!sent ? (
            <>
              <h2 className="text-2xl lg:text-4xl font-bold text-gray-900 mb-3 text-center">
                Forgot Your Password?
              </h2>

              <p className="text-gray-500 mb-10 text-center">
                Enter the email associated with your account and we'll send a link to reset your password.
              </p>

              {error && <p className="text-red-500 text-center mb-4">{error}</p>}

              <div className="space-y-5">
                <div>
                  <label className="text-sm font-medium mb-1 block">Email Address</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                <Button className="w-full bg-black text-white py-3" onClick={handleSend}>
                  Send reset link
                </Button>
              </div>
            </>
          ) : (
            <>
              <h2 className="text-2xl lg:text-4xl font-bold mb-3 text-center">Check Your Email</h2>
              <p className="text-gray-600 mb-6 text-center">
                We've sent a password reset link to <strong>{email}</strong>.
                This might take a moment. If it doesn't appear, please try again.
              </p>

              <Button className="w-full bg-black text-white py-3 mb-6" onClick={handleOpenEmail}>
                Open email
              </Button>

              {counter > 0 ? (
                <p className="text-gray-500 text-center">
                  Resend link <strong>({counter}s)</strong>
                </p>
              ) : (
                <button className="text-black underline mx-auto block" onClick={handleSend}>
                  Resend link
                </button>
              )}

              {/* BOTTOM: Don't have an account? Sign Up */}
              <div className="relative flex items-center justify-center mt-20 mb-4">
                <div className="flex-grow h-px bg-gray-300"></div>
                <p className="mx-4 text-sm text-gray-600">
                  Don't have an account?{" "}
                  <Link to="/signup" className="font-medium text-black hover:underline">
                    Sign Up
                  </Link>
                </p>
                <div className="flex-grow h-px bg-gray-300"></div>
              </div>
            </>
          )}

          {/* BOTTOM: Need Help? Contact Support - Only on Forgot Password page */}
          {!sent && (
            <div className="relative flex items-center justify-center mt-20 mb-6">
              <div className="flex-grow h-px bg-gray-300"></div>
              <p className="mx-4 text-sm text-gray-600">
                Need Help?{" "}
                <Link to="/signup" className="font-medium text-black hover:underline">
                  Contact Support
                </Link>
              </p>
              <div className="flex-grow h-px bg-gray-300"></div>
            </div>
          )}
        </div>
      </div>

      {/* RIGHT VIDEO SECTION — identical to login page */}
      <div className="hidden lg:flex w-1/2 relative">
        {videos.map((src, index) => (
          <video
            key={index}
            src={src}
            muted
            autoPlay
            loop
            playsInline
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
              currentVideo === index ? "opacity-100" : "opacity-0"
            }`}
          />
        ))}

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>

        {/* Bottom Captions */}
        <div className="absolute bottom-10 left-0 w-full text-center text-white z-20 px-10">
          {currentVideo === 0 && (
            <>
              <h3 className="text-2xl font-bold">Editor</h3>
              <p className="text-sm opacity-90 mt-2">
                Bring your creative vision to life with intuitive editing tools.
              </p>
            </>
          )}

          {currentVideo === 1 && (
            <>
              <h3 className="text-2xl font-bold">Photography</h3>
              <p className="text-sm opacity-90 mt-2">
                Join a lively network of skilled photographers and discover amazing shooting opportunities!
              </p>
            </>
          )}

          {currentVideo === 2 && (
            <>
              <h3 className="text-2xl font-bold">Videography</h3>
              <p className="text-sm opacity-90 mt-2">
                Connect with a vibrant community of talented videographers and unlock amazing shooting opportunities!
              </p>
            </>
          )}

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`w-2 h-2 rounded-full ${
                  currentVideo === i ? "bg-white" : "bg-white/60"
                }`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
